/**
 * SEO Canonical URL Utility
 *
 * Generates fully-qualified canonical URLs for the metadata engine.
 * Strips tracking/UTM query parameters per spec seo-003 requirement 3.
 * Supports environment-based base domain configuration.
 */

/** Tracking parameters that must never appear in canonical URLs */
const TRACKING_PARAMS = new Set([
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "ref",
  "fbclid",
  "gclid",
  "mc_cid",
  "mc_eid",
  "_ga",
]);

/**
 * Resolve the site base URL from environment variables or the browser origin.
 * Priority: REACT_APP_BASE_URL env var → window.location.origin → hardcoded fallback.
 */
export const getSiteBaseUrl = (): string => {
  if (process.env.REACT_APP_BASE_URL) {
    // Strip trailing slash for consistent concatenation
    return process.env.REACT_APP_BASE_URL.replace(/\/$/, "");
  }
  if (typeof window !== "undefined" && window.location?.origin) {
    // Avoid localhost canonicals during prerender / local snapshots
    const isLocalhost =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1" ||
      navigator.userAgent === "ReactSnap" ||
      (window as any).__snapshot;

    if (isLocalhost) {
      return "https://moderatepolitics.org";
    }
    return window.location.origin;
  }
  return "https://moderatepolitics.org";
};

/**
 * Build a fully-qualified canonical URL from a pathname.
 * Strips all tracking query parameters.
 *
 * @param pathname - The route pathname (e.g. "/conversations-nearby/123")
 * @param searchString - Raw query string (optional, e.g. "?utm_source=twitter&tab=active")
 * @returns Fully-qualified canonical URL with clean query string
 *
 * @example
 * buildCanonicalUrl('/conversations-nearby/room-456', '?utm_source=twitter&ref=share')
 * // → 'https://moderatepolitics.org/conversations-nearby/room-456'
 */
export const buildCanonicalUrl = (
  pathname: string,
  searchString?: string
): string => {
  const base = getSiteBaseUrl();

  if (!searchString) {
    return `${base}${pathname}`;
  }

  // Strip known tracking params; keep legitimate params intact
  const params = new URLSearchParams(searchString);
  TRACKING_PARAMS.forEach((key) => params.delete(key));

  const cleanQuery = params.toString();
  return cleanQuery ? `${base}${pathname}?${cleanQuery}` : `${base}${pathname}`;
};
